import { compact } from "lodash";
import { CalendarMode, CalendarView, Row, Warnings } from "PFApp/booking/types";
import { ProjectItemResource, RowType } from "PFApp/booking/types/row";
import { minutesToHours } from "PFCore/helpers/date";
import { useMemo } from "react";
import { TFunction, useTranslation } from "react-i18next";

import { calculateHoursBooked, getChildrenCount, getOriginalResource } from "../../helpers";
import { WorkforceCalendarData } from "../context/booking_overview_context.types";
import { useBookingRowsContext } from "../context/booking_rows_context";
import { useBookingsOverviewContext } from "../context/bookings_overview_context";

interface GetInfoText {
  row: Row;
  calendarMode: CalendarMode;
  t: TFunction<"bookingModule">;
  hoursBooked: number;
  warnings?: Warnings;
}

const getInfoText = ({ row, calendarMode, hoursBooked, warnings, t }: GetInfoText) => {
  const { type } = row;

  if ((type === RowType.Role || type === RowType.Engagement) && calendarMode === CalendarMode.Projects) {
    const { booked, total } = getChildrenCount(row);
    return `${booked}/${total} ${t(`projects.${type === RowType.Role ? "vacancies" : "roles"}Booked`)}`;
  }

  if (type === RowType.Profile) {
    const hoursAvailable = minutesToHours(warnings?.available_minutes);
    return calendarMode === CalendarMode.Workforce
      ? t("projects.hoursAvailable", { hours: hoursAvailable })
      : t("projects.bookedFor", { hours: hoursBooked || 0 });
  }

  return null;
};

interface InfoTextProps {
  row: Row;
}

const DELIMITER = " · ";

export const useInfoText = ({ row }: InfoTextProps): string | null => {
  const { t } = useTranslation("bookingModule");
  const {
    calendarMode,
    calendarData: { view }
  } = useBookingsOverviewContext<WorkforceCalendarData>();
  const { warningsByProfile } = useBookingRowsContext();
  const { bookingsByRowKey } = useBookingRowsContext();

  return useMemo(() => {
    const { key, type } = row;
    const original = getOriginalResource(row, calendarMode);
    const profileId = type === RowType.Profile ? original?.id : null;
    const parentActivityName =
      type === RowType.Role &&
      (view === CalendarView.RolesAndAuditRoles || calendarMode === CalendarMode.Workforce)
        ? (original as ProjectItemResource).parent_activity?.name
        : null;
    const hoursBooked = calculateHoursBooked(bookingsByRowKey[key]);
    const infoText = getInfoText({
      row,
      calendarMode,
      t,
      warnings: warningsByProfile && profileId ? warningsByProfile[profileId] : undefined,
      hoursBooked
    });
    return compact([parentActivityName, infoText]).join(DELIMITER);
  }, [row, calendarMode, warningsByProfile, bookingsByRowKey, t, view]);
};
